import { useQuery } from "@tanstack/react-query";
import type { ProductLeadCountsResponse } from "@school-sales-ai/shared";
import { api } from "@/lib/api";

/**
 * Contextual filters that shape the product counts. Never includes `productIds`
 * — counts are grouped across all products. Lists are comma-joined.
 */
export type ProductCountFilters = {
  stages?: string;
  channel?: string;
  scoreMin?: string;
  scoreMax?: string;
  cycleIds?: string;
  search?: string;
  from?: string;
  to?: string;
  // Comma-joined label UUIDs — keeps product counts predictive under a label filter.
  labelIds?: string;
  // Comma-joined assignee ids («Asignado a»), "none" = unassigned.
  assigneeIds?: string;
};

export function useProductLeadCounts(
  filters: ProductCountFilters,
): ReturnType<typeof useQuery<ProductLeadCountsResponse>> {
  const entries = Object.entries(filters).filter(
    ([, v]) => v !== "" && v !== undefined,
  );
  const qs = new URLSearchParams(
    entries.map(([k, v]) => [k, String(v)]),
  ).toString();

  return useQuery<ProductLeadCountsResponse>({
    queryKey: ["product-counts", filters],
    queryFn: () =>
      api.get<ProductLeadCountsResponse>(
        `/crm/leads/product-counts${qs ? `?${qs}` : ""}`,
      ),
    staleTime: 60_000,
  });
}
